import React, { useRef, useState, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';
import { isNative, hapticImpact } from '../native';

const PULL_THRESHOLD = 72; // px of pull before a release triggers refresh
const MAX_PULL = 110;

/**
 * Native-style pull-to-refresh. Only active on device -- on web this just
 * renders children, since pull-to-refresh isn't an expected desktop-browser
 * gesture and could fight the page's normal scroll.
 */
export const PullToRefresh = ({ onRefresh, children }) => {
  const native = isNative();
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef(null);
  const containerRef = useRef(null);

  const handleTouchStart = useCallback((e) => {
    if (refreshing) return;
    // Only begin tracking if the page is already scrolled to the top --
    // otherwise this would hijack normal scrolling mid-list.
    if (window.scrollY > 4) return;
    startY.current = e.touches[0].clientY;
  }, [refreshing]);

  const handleTouchMove = useCallback((e) => {
    if (startY.current == null || refreshing) return;
    const delta = e.touches[0].clientY - startY.current;
    if (delta > 0 && window.scrollY <= 4) {
      setPull(Math.min(delta * 0.5, MAX_PULL));
    }
  }, [refreshing]);

  const handleTouchEnd = useCallback(async () => {
    if (startY.current == null) return;
    startY.current = null;

    if (pull >= PULL_THRESHOLD) {
      setRefreshing(true);
      hapticImpact('medium');
      setPull(PULL_THRESHOLD);
      try {
        await onRefresh?.();
      } finally {
        setRefreshing(false);
        setPull(0);
      }
    } else {
      setPull(0);
    }
  }, [pull, onRefresh]);

  if (!native) return <>{children}</>;

  const progress = Math.min(pull / PULL_THRESHOLD, 1);

  return (
    <div
      ref={containerRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div
        className="flex items-center justify-center overflow-hidden transition-[height] duration-200"
        style={{ height: pull }}
        aria-hidden={pull === 0}
      >
        <RefreshCw
          className={`w-5 h-5 text-primary ${refreshing ? 'animate-spin' : ''}`}
          style={{
            transform: refreshing ? undefined : `rotate(${progress * 360}deg)`,
            opacity: progress,
          }}
        />
      </div>
      {children}
    </div>
  );
};
